// Pure STAGING logic for the Work card's two-step verdict flow (brief §3.5/§5.3). Ticks are client
// VIEW STATE only — nothing here records anything, and no approval field ever leaves this module
// (P-GATE). It compiles block-level ticks into the ONE Execute wire payload (the selected sibling
// per staged block — object ids + verdicts + mandatory reject reasons only), derives the live
// plain-English summary the Execute bar shows, and detects INCOHERENT staging across linked
// proposals (a staged approve whose `dependsOn` target is staged reject or left unruled) so the
// surface disables Execute with an honest explanation instead of routing a doomed commit.

import type { DecisionBlockView, IntentionView, StagedVerdictWire } from '../projections.ts';

/** One block's staged tick. A reject always carries its mandatory reason (G-055 shape). */
export type StageTick =
  | { readonly verdict: 'approve' }
  | { readonly verdict: 'reject'; readonly reason: string };

/** Block key → staged tick (undefined = unruled). Keys come from `blockKeyOf`. */
export type StageMap = Readonly<Record<string, StageTick | undefined>>;

/** Block key → the selected option id within a choice set (standalone blocks have one option). */
export type SelectionMap = Readonly<Record<string, string | undefined>>;

/** A stable staging/render key for one decision block. */
export function blockKeyOf(block: DecisionBlockView): string {
  return block.choiceSet ?? block.options[0]?.id ?? 'block';
}

/** The block's still-open options (status derives from the owning module's round — G-182). */
export function openOptionsOf(block: DecisionBlockView): readonly IntentionView[] {
  return block.options.filter((option) => option.status === 'proposed');
}

/** Find one proposal by id across every block of the round (open or ruled). */
export function findOption(
  blocks: readonly DecisionBlockView[],
  intentionId: string,
): IntentionView | null {
  for (const block of blocks) {
    const match = block.options.find((option) => option.id === intentionId);
    if (match !== undefined) return match;
  }
  return null;
}

/** The selected sibling: the explicit selection when still open, else the first open option
 *  (options arrive recommended-first, so the recommended one is pre-selected — brief §5.3.2). */
export function selectedOptionOf(
  block: DecisionBlockView,
  selection: SelectionMap,
): IntentionView | null {
  const open = openOptionsOf(block);
  const chosen = selection[blockKeyOf(block)];
  return open.find((option) => option.id === chosen) ?? open[0] ?? null;
}

/** Compile the staged ticks into the Execute wire payload — the SELECTED sibling per staged block,
 *  in block order. Selecting a different sibling re-points the block's tick (the tick follows the
 *  selection); an unruled block contributes nothing. */
export function compileStaged(
  blocks: readonly DecisionBlockView[],
  selection: SelectionMap,
  stage: StageMap,
): StagedVerdictWire[] {
  const staged: StagedVerdictWire[] = [];
  for (const block of blocks) {
    const tick = stage[blockKeyOf(block)];
    const option = selectedOptionOf(block, selection);
    if (tick === undefined || option === null) continue;
    staged.push(
      tick.verdict === 'reject'
        ? { intentionId: option.id, verdict: 'reject', reason: tick.reason }
        : { intentionId: option.id, verdict: 'approve' },
    );
  }
  return staged;
}

/** The staged verdict currently pointing at one proposal id, if any block's selected tick does. */
function stagedVerdictOn(
  blocks: readonly DecisionBlockView[],
  selection: SelectionMap,
  stage: StageMap,
  intentionId: string,
): StageTick['verdict'] | null {
  for (const block of blocks) {
    const tick = stage[blockKeyOf(block)];
    if (tick === undefined) continue;
    const option = selectedOptionOf(block, selection);
    if (option !== null && option.id === intentionId) return tick.verdict;
  }
  return null;
}

/** An incoherently-staged linked proposal, named for the honest inline explanation. */
export interface StagingInconsistency {
  readonly dependentTitle: string;
  readonly dependencyTitle: string;
}

/** Detect incoherent staging: a staged APPROVE whose `dependsOn` target is neither already
 *  approved/executed nor staged approve (i.e. staged reject, or left unruled). A dependency the
 *  round does not carry cannot be judged here — the owning module's commit rules on it. */
export function stagedInconsistency(
  blocks: readonly DecisionBlockView[],
  selection: SelectionMap,
  stage: StageMap,
): StagingInconsistency | null {
  for (const block of blocks) {
    const tick = stage[blockKeyOf(block)];
    if (tick === undefined || tick.verdict !== 'approve' || block.dependsOn === null) continue;
    const dependency = findOption(blocks, block.dependsOn);
    if (dependency === null) continue;
    if (dependency.status === 'approved' || dependency.status === 'executed') continue;
    if (stagedVerdictOn(blocks, selection, stage, dependency.id) === 'approve') continue;
    const dependent = selectedOptionOf(block, selection);
    return {
      dependentTitle: dependent?.title ?? 'this proposal',
      dependencyTitle: dependency.title,
    };
  }
  return null;
}

/** The live staged summary the Execute bar renders (brief §5.3.2 — plain English, from the ticks). */
export interface StagedSummary {
  readonly text: string;
  readonly approves: number;
  readonly rejects: number;
}

/** Summarise what one Execute press would commit: sends grouped by count, other approves named by
 *  their proposal title, rejects counted. Empty text = nothing staged yet. */
export function stagedSummaryOf(
  blocks: readonly DecisionBlockView[],
  selection: SelectionMap,
  stage: StageMap,
): StagedSummary {
  const approved: IntentionView[] = [];
  let rejects = 0;
  for (const block of blocks) {
    const tick = stage[blockKeyOf(block)];
    const option = selectedOptionOf(block, selection);
    if (tick === undefined || option === null) continue;
    if (tick.verdict === 'approve') approved.push(option);
    else rejects += 1;
  }
  const phrases: string[] = [];
  const emails = approved.filter((option) => option.actionType === 'mail.send').length;
  if (emails > 0) phrases.push(emails === 1 ? 'Send 1 email' : `Send ${String(emails)} emails`);
  for (const option of approved) {
    if (option.actionType !== 'mail.send') phrases.push(option.title);
  }
  if (rejects > 0) {
    phrases.push(rejects === 1 ? 'Reject 1 proposal' : `Reject ${String(rejects)} proposals`);
  }
  return { text: phrases.join(' · '), approves: approved.length, rejects };
}
